/**
 * `src/lib/ui/nav/transition.ts` — one route transition, run from `onNavigate`.
 *
 * Same-document View Transitions, Level 1 only: the whole page is the `root`
 * image, and `html[data-nav]` tells `app.css` which motion to play (push, pop
 * or fade). The DOM itself is never transformed, so the reader's
 * `getBoundingClientRect` / `scrollMargin` maths and every fixed and sticky
 * surface keep their real layout.
 *
 * Kit's order is `load`, then `onNavigate` (awaited), then the DOM update, the
 * scroll, `complete` and `afterNavigate`. The update callback below releases
 * Kit and then awaits `complete`, so the new snapshot is the final, scrolled
 * page. It never awaits the reader's landing, which can take a second.
 *
 * THE PROMISE ALWAYS RESOLVES: in the update callback, in the `catch`, or by
 * the 400 ms safety timeout if the browser never calls back. A navigation can
 * never freeze behind a transition.
 *
 * Exactly three elements carry a `view-transition-name`, each mounted once in
 * the layout: `tab-bar` (TabBar), `mini-player` (MiniPlayer) and
 * `update-toast` (UpdateToast). No other element may declare one; a duplicate
 * aborts every transition. Anything may test `html[data-nav]` to know whether
 * a transition is on screen.
 *
 * These get today's instant swap instead: a browser without
 * `startViewTransition`, a hidden tab, reduced motion (`app.css` also carries a
 * CSS backstop), `deviceMemory < 2`, and the two kill switches.
 */

import type { OnNavigate } from '@sveltejs/kit';

import { prefersReducedMotion } from '$lib/ui/theme';

import type { NavKind } from './nav-model';

/** Kill switch: `false` = instant swaps everywhere. */
export const VIEW_TRANSITIONS = true;
/** Kill switch for iOS WebKit only. */
export const VIEW_TRANSITIONS_ON_IOS = true;

const START_TIMEOUT_MS = 400;

/* Only the latest transition may clear `data-nav`: a newer one has already set
 * its own, and the older one finishing late must not erase it. */
let token = 0;

export function runTransition(n: OnNavigate, kind: NavKind): Promise<void> | undefined {
	if (!VIEW_TRANSITIONS || kind === 'none') return;
	// `in`, never truthiness: the DOM lib declares the method (TS2774).
	if (typeof document === 'undefined' || !('startViewTransition' in document)) return;
	if (document.visibilityState !== 'visible' || prefersReducedMotion(window)) return;
	const memory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
	if (memory !== undefined && memory < 2) return;
	if (!VIEW_TRANSITIONS_ON_IOS && CSS.supports('-webkit-touch-callout', 'none')) return;

	const root = document.documentElement;
	const mine = ++token;
	const done = () => {
		if (token === mine) delete root.dataset.nav;
	};

	return new Promise<void>((resolve) => {
		const safety = setTimeout(resolve, START_TIMEOUT_MS);
		root.dataset.nav = kind;
		try {
			const vt = document.startViewTransition(async () => {
				clearTimeout(safety);
				resolve();
				await n.complete.catch(() => {});
			});
			vt.ready.catch(() => {});
			vt.updateCallbackDone.catch(() => {});
			vt.finished.catch(() => {}).finally(done);
		} catch {
			clearTimeout(safety);
			done();
			resolve();
		}
	});
}
